import React from 'react';
import Reveal from '../components/Reveal';
import ContactForm from '../components/ContactForm';
import usePageTitle from '../hooks/usePageTitle';
import { images, aboutSections, ceoBio, boardRoles, booking } from '../mock';
import { useBooking } from '../context/BookingContext';
import { Check, ArrowRight, GraduationCap, Users, Heart } from 'lucide-react';

const PageHero = ({ eyebrow, title, sub, image }) => (
  <section className="relative pt-40 pb-24">
    <div className="absolute inset-0">
      <img src={image} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#241033]/95 via-[#2C1743]/85 to-[#43265E]/60" />
    </div>
    <div className="relative max-w-[1280px] mx-auto px-6">
      <Reveal>
        <div className="eyebrow text-silver mb-5">{eyebrow}</div>
        <h1 className="font-display text-5xl md:text-6xl text-white font-medium leading-tight max-w-3xl">{title}</h1>
        {sub && <p className="text-[#D9CEE6] font-light text-lg mt-6 max-w-xl">{sub}</p>}
      </Reveal>
    </div>
  </section>
);

const About = () => {
  usePageTitle('About');
  const { openBooking } = useBooking();
  return (
    <main>
      <section className="pt-20 md:pt-24 bg-[#FBFAF8]">
        <img
          src={images.aboutBanner}
          alt="Leading People. Shaping Possibilities. — The Answer Group Consulting"
          className="w-full h-auto block"
        />
      </section>

      {/* Who we are */}
      <section className="py-24 bg-[#FBFAF8]">
        <div className="max-w-[1000px] mx-auto px-6">
          <Reveal>
            <div className="eyebrow text-[#7E5C9E] mb-6">Who We Are</div>
            <p className="font-display text-3xl md:text-4xl text-[#241033] leading-snug font-medium">
              {aboutSections.whoWeAre}
            </p>
          </Reveal>
        </div>
      </section>

      {/* What we do */}
      <section className="py-24 bg-[#241033]">
        <div className="max-w-[1280px] mx-auto px-6">
          <Reveal>
            <div className="eyebrow text-[#7E5C9E] mb-6">What We Do</div>
            <h2 className="font-display text-4xl md:text-5xl text-white font-medium mb-16 max-w-2xl">We work across sectors—partnering with purpose.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {aboutSections.whatWeDo.map((item, i) => {
              const Icon = [Users, Heart, GraduationCap][i];
              return (
                <Reveal key={item.h} delay={i * 100}>
                  <div className="card-lift bg-[#2C1743]/60 border border-[#43265E]/50 rounded-sm p-8 h-full">
                    <div className="w-12 h-12 rounded-full bg-silver flex items-center justify-center mb-6">
                      <Icon className="w-5 h-5 text-[#241033]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display text-2xl text-white mb-3">{item.h}</h3>
                    <p className="text-[#B9A9CC] font-light leading-relaxed text-sm">{item.p}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* How we work + why we exist */}
      <section className="py-24 bg-[#F4F1EC]">
        <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <div className="eyebrow text-[#7E5C9E] mb-6">How We Work</div>
            <p className="text-[#4A4453] font-light text-lg mb-8">Our approach blends the science of systems with the soul of service. We don’t believe in one-size-fits-all. We believe in:</p>
            <ul className="space-y-4">
              {aboutSections.howWeWork.map((h) => (
                <li key={h} className="flex items-center gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#43265E] flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-white" strokeWidth={2} />
                  </span>
                  <span className="font-display text-2xl text-[#241033]">{h}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={150}>
            <div className="bg-[#43265E] rounded-sm p-12 h-full flex flex-col justify-center">
              <div className="eyebrow text-[#B9A9CC] mb-6">Why We Exist</div>
              <p className="font-display text-3xl text-white leading-snug italic">{aboutSections.whyWeExist}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Meet the CEO */}
      <section className="py-28 bg-[#FBFAF8]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <Reveal>
                  <div className="relative">
                    <div className="absolute -inset-4 bg-silver rounded-sm opacity-60" />
                    <img src={images.headshot} alt="Janiece A. Jean Baptiste" className="relative w-full object-cover rounded-sm bg-[#EDE8F2]" />
                  </div>
                  <div className="mt-8">
                    <div className="eyebrow text-[#7E5C9E] mb-2">Meet the CEO</div>
                    <h3 className="font-display text-3xl text-[#241033] font-semibold">Janiece A. Jean Baptiste, M.S.</h3>
                    <p className="text-[#6A6570] font-light mt-1">I/O Psychology Practitioner · Educator · Strategist · Coach</p>
                    <ul className="mt-6 space-y-2">
                      {boardRoles.map((r) => (
                        <li key={r} className="text-sm text-[#4A4453] font-light flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#43265E]" /> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="space-y-6">
                {ceoBio.map((p, i) => (
                  <Reveal key={p.slice(0, 32)} delay={Math.min(i * 40, 200)}>
                    <p className={`font-light leading-relaxed ${i === 0 ? 'font-display text-2xl text-[#43265E] not-italic' : 'text-[#4A4453] text-[1.02rem]'}`}>
                      {p}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-28 bg-[#F4F1EC]">
        <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <div className="eyebrow text-[#7E5C9E] mb-6">Contact Us</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium leading-tight mb-6">Let’s build something impactful—together.</h2>
            <p className="text-[#4A4453] font-light text-lg mb-8">
              Interested in working together? Fill out the form and we’ll get in touch to start the conversation. Prefer to choose a time now?
            </p>
            <button onClick={() => openBooking(booking.alignmentLab, 'Book a Session')} className="btn-outline rounded-full px-8 py-3.5 text-sm tracking-luxe uppercase inline-flex items-center gap-2">
              Book a Session <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </Reveal>
          <Reveal delay={150}>
            <div className="bg-white rounded-sm p-8 md:p-10 shadow-[0_30px_60px_-40px_rgba(36,16,51,0.4)]">
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default About;
